import { AfterViewInit, Component, EventEmitter, OnDestroy, OnInit, Output, ViewChild } from '@angular/core';
import { MatStepper } from '@angular/material/stepper';
import { ResourceManagerService, ResourceSet } from '@barthauer/abc/core/resources';
import { DataEntity } from '@barthauer/abc/graphics/adapters/open-layers';
import {
  MessageBoxButtons,
  MessageBoxIcon,
  MessageBoxService,
  MessageMode,
  MessageSettings,
} from '@barthauer/abc/messages';
import { Observable, Subject } from 'rxjs';
import { delay, finalize, switchMap, takeUntil } from 'rxjs/operators';
import { ImWorkService } from '../create-immediate-measure-wizard/services/imwork.service';
import { ResultWorkGeneration } from '../create-works-wizard/models/helpers';
import { MtWorkTypeSimple } from '../create-works-wizard/models/MtWorkType';

@Component({
  selector: 'basys-opera-wizards-create-immediate-measure-wizard',
  templateUrl: './create-immediate-measure-wizard.component.html',
  styleUrls: ['./create-immediate-measure-wizard.component.scss'],
  standalone: false,
})
export class CreateImmediateMeasureWizardComponent implements OnInit, AfterViewInit, OnDestroy {
  @ViewChild('stepper') stepper: MatStepper;
  @Output() closeWizardDialog = new EventEmitter();
  @Output() showWork = new EventEmitter<{ workId: string; show: boolean }>();
  public res$: Observable<ResourceSet>;
  public res: ResourceSet = ResourceSet.Empty;
  public editResultsImmediatly = true;
  public disableDataContinueButton: boolean = true;
  public display = false;
  public buttonStatus = false;
  public workTypeData: MtWorkTypeSimple[];
  private lastGeneratedWorkId: string;
  private defaultOrderId: string;
  private selectedDataEntity: DataEntity;
  private selectedWorkType: any;
  private unsubscribe$ = new Subject<void>();
  isGenerating: boolean = false;
  public isLoadingWorkTypes = true;

  constructor(
    private imWorkService: ImWorkService,
    private messageBoxService: MessageBoxService,
    private resourceService: ResourceManagerService,
  ) {
    this.res$ = this.resourceService.getResourceSetAsObservable('opera-wizards');
  }

  async ngOnInit(): Promise<void> {
    this.res = await this.res$.toPromise();
  }

  ngAfterViewInit(): void {
    this.bindingEvents();
  }

  ngOnDestroy(): void {
    this.unsubscribe$.next(null);
    this.unsubscribe$.complete();
  }

  private bindingEvents(): void {
    this.imWorkService.imWorkGenerationFailed
      .pipe(takeUntil(this.unsubscribe$))
      .subscribe((err) => this.toastErrorMessage(err));
    this.imWorkService.imWorkGenerationSucceeded
      .pipe(takeUntil(this.unsubscribe$))
      .subscribe((result) => this.onImWorkGenerated(result));

    this.imWorkService
      .queryRelevantWorkTypesWithDateFilter(this.selectedDataEntity.objectType, this.selectedDataEntity)
      .pipe(
        takeUntil(this.unsubscribe$),
        switchMap((result) => this.imWorkService.queryWorkTypeObjectDefinitions(result)),
        finalize(() => (this.isLoadingWorkTypes = false)),
      )
      .subscribe(
        (filtered) => {
          this.workTypeData = filtered;
        },
        (err) => this.toastErrorMessage(err),
      );
  }

  private toastErrorMessage(err: any): void {
    let message = err.message;
    this.showMessageAsHtml(message, MessageMode.Error);
  }

  private onImWorkGenerated(result: ResultWorkGeneration): void {
    this.lastGeneratedWorkId = result.generatedWorks?.length ? <string>result.generatedWorks[0].Id : '';
    if (this.lastGeneratedWorkId) {
      this.showWork.emit({ workId: this.lastGeneratedWorkId, show: this.editResultsImmediatly });
      this.closeWizardDialog.emit();
    } else {
      this.toastErrorMessage(this.res.get('TheWorksExistsForTheDate'));
    }
    this.isGenerating = false;
  }

  public init(entities: DataEntity[], orderId: string): void {
    this.selectedDataEntity = entities[0];
    this.defaultOrderId = orderId;
  }

  public moveStep(stepper: MatStepper): void {
    if (this.selectedWorkType == null) {
      const ret = this.res.get('PleaseSelectWorkTemplate');
      this.showMessageAsHtml(ret, MessageMode.Info);
    } else {
      stepper.next();
    }
  }

  private showMessageAsHtml(htmlMsg: string, messageMode: MessageMode = MessageMode.Info): void {
    const settings = new MessageSettings();
    settings.mode = messageMode;
    let icon = MessageBoxIcon.Info;
    switch (messageMode) {
      case MessageMode.Error:
        icon = MessageBoxIcon.Error;
        break;
      case MessageMode.Warning:
        icon = MessageBoxIcon.Warning;
        break;
      case MessageMode.Question:
        icon = MessageBoxIcon.Question;
        break;
    }
    settings.icon = icon;
    settings.button = MessageBoxButtons.OK;
    settings.htmlcode = document.createElement('div');
    settings.htmlcode.innerHTML = htmlMsg;

    this.messageBoxService.showMessageBox(settings).subscribe();
  }

  public async execute(): Promise<void> {
    this.display = !this.display;
    this.buttonStatus = !this.buttonStatus;
    this.disableDataContinueButton = !this.disableDataContinueButton;
    this.imWorkService.generateImWork(this.selectedDataEntity, this.selectedWorkType, this.defaultOrderId);
  }

  public listSelectionChanged = (e: any) => {
    this.selectedWorkType = e.addedItems[0];
    this.disableDataContinueButton = this.selectedWorkType === undefined;
  };

  public toggleEditResult(checked: boolean): void {
    this.editResultsImmediatly = !checked;
  }
}
